import React, { useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getWebsite,
  listKeywords,
  deleteWebsite,
  deleteKeyword,
  checkKeyword,
  checkAll,
  getCheckJob,
  getActiveJob,
  exportCsvUrl,
  reportPdfUrl,
} from "@/lib/api";
import AddKeywordsDialog from "@/components/AddKeywordsDialog";
import ShareButton from "@/components/ShareButton";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import AiVisibilityTab from "@/components/AiVisibilityTab";
import { toast } from "sonner";
import {
  ArrowDown,
  ArrowUp,
  ArrowsClockwise,
  CaretLeft,
  Copy,
  DownloadSimple,
  FilePdf,
  Minus,
  Trash,
} from "@phosphor-icons/react";

const MAX_KEYWORDS = 200;

function RankDelta({ current, previous }) {
  if (current == null && previous == null) return <span className="text-[var(--rt-text-muted)]">—</span>;
  if (current == null) return <span className="text-[var(--rt-danger)] text-xs font-mono-data">LOST</span>;
  if (previous == null) return <span className="text-[var(--rt-text-muted)] text-xs font-mono-data">NEW</span>;
  const delta = previous - current; // positive = improved (smaller rank #)
  if (delta === 0)
    return (
      <span className="text-[var(--rt-text-muted)] inline-flex items-center gap-1 text-xs font-mono-data">
        <Minus size={12} /> 0
      </span>
    );
  if (delta > 0)
    return (
      <span className="text-[var(--rt-success)] inline-flex items-center gap-1 text-xs font-mono-data">
        <ArrowUp size={12} weight="bold" /> {delta}
      </span>
    );
  return (
    <span className="text-[var(--rt-danger)] inline-flex items-center gap-1 text-xs font-mono-data">
      <ArrowDown size={12} weight="bold" /> {Math.abs(delta)}
    </span>
  );
}

function PositionBadge({ position }) {
  if (position == null)
    return <span className="font-mono-data text-[var(--rt-text-muted)]">—</span>;
  let color = "text-[var(--rt-text)]";
  if (position <= 3) color = "text-[var(--rt-success)]";
  else if (position <= 10) color = "text-[var(--rt-primary)]";
  else if (position > 50) color = "text-[var(--rt-text-muted)]";
  return <span className={`font-mono-data text-lg font-semibold ${color}`}>{position}</span>;
}

export default function WebsiteDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [filter, setFilter] = useState("all");

  const { data: site } = useQuery({ queryKey: ["website", id], queryFn: () => getWebsite(id) });
  const { data: keywords = [], isLoading } = useQuery({
    queryKey: ["keywords", id],
    queryFn: () => listKeywords(id),
  });

  const filtered = useMemo(() => {
    return keywords.filter((k) => {
      const p = k.last_position;
      if (filter === "top3") return p != null && p <= 3;
      if (filter === "top10") return p != null && p <= 10;
      if (filter === "top50") return p != null && p <= 50;
      if (filter === "missing") return p == null;
      return true;
    });
  }, [keywords, filter]);

  // Active job polling - sets state on page load, then refreshes every 1.5s while running
  const [jobId, setJobId] = useState(null);

  const { data: activeJobData } = useQuery({
    queryKey: ["active-job", id],
    queryFn: () => getActiveJob(id),
    refetchOnWindowFocus: false,
    enabled: !jobId,
  });

  React.useEffect(() => {
    if (activeJobData?.job?.id && !jobId) setJobId(activeJobData.job.id);
  }, [activeJobData, jobId]);

  const { data: jobStatus } = useQuery({
    queryKey: ["job", jobId],
    queryFn: () => getCheckJob(jobId),
    enabled: !!jobId,
    refetchInterval: (query) => {
      const s = query?.state?.data?.status;
      return s === "queued" || s === "running" ? 1500 : false;
    },
  });

  // When job completes, refresh data and toast
  React.useEffect(() => {
    if (!jobStatus || !jobId) return;
    if (jobStatus.status === "done") {
      qc.invalidateQueries({ queryKey: ["keywords", id] });
      qc.invalidateQueries({ queryKey: ["website", id] });
      qc.invalidateQueries({ queryKey: ["websites"] });
      const failed = jobStatus.failed || 0;
      const total = jobStatus.total || 0;
      if (failed > 0 && failed === total) {
        toast.error(`All ${total} checks failed: ${jobStatus.errors?.[0]?.error || "unknown error"}`);
      } else if (failed > 0) {
        toast.warning(`Checked ${total - failed}/${total} · ${failed} failed`);
      } else {
        toast.success(`Checked ${total}/${total} keyword(s)`);
      }
      setJobId(null);
    } else if (jobStatus.status === "error") {
      toast.error(jobStatus.error || "Job failed");
      setJobId(null);
    } else {
      // While running, keep keywords fresh so positions stream in
      qc.invalidateQueries({ queryKey: ["keywords", id] });
    }
  }, [jobStatus, jobId, id, qc]);

  const checkAllMut = useMutation({
    mutationFn: () => checkAll(id),
    onSuccess: (data) => {
      if (data?.job_id) {
        setJobId(data.job_id);
        if (data.already_running) toast.info("A check is already running");
        else toast.success(`Checking ${data.total} keyword(s) in the background…`);
      }
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed to start check"),
  });

  const checkOneMut = useMutation({
    mutationFn: (keywordId) => checkKeyword(keywordId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["keywords", id] });
      qc.invalidateQueries({ queryKey: ["website", id] });
      qc.invalidateQueries({ queryKey: ["websites"] });
      toast.success("Rank updated");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Check failed"),
  });

  const delKwMut = useMutation({
    mutationFn: (keywordId) => deleteKeyword(keywordId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["keywords", id] });
      qc.invalidateQueries({ queryKey: ["website", id] });
      qc.invalidateQueries({ queryKey: ["websites"] });
      toast.success("Keyword removed");
    },
  });

  const delSiteMut = useMutation({
    mutationFn: () => deleteWebsite(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["websites"] });
      toast.success("Website deleted");
      navigate("/dashboard", { replace: true });
    },
  });

  const remaining = MAX_KEYWORDS - (keywords?.length || 0);
  const isJobRunning = jobStatus?.status === "queued" || jobStatus?.status === "running";
  const jobPct = jobStatus?.total ? Math.round(((jobStatus.completed || 0) / jobStatus.total) * 100) : 0;

  return (
    <div className="max-w-[1600px] mx-auto px-6 py-8">
      <Link
        to="/dashboard"
        className="inline-flex items-center gap-1 text-xs label-eyebrow text-[var(--rt-text-muted)] hover:text-[var(--rt-text)] mb-4"
        data-testid="back-to-dashboard"
      >
        <CaretLeft size={14} /> all websites
      </Link>

      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <div className="label-eyebrow">/ site</div>
          <h1 className="font-display text-5xl tracking-tight leading-none mt-2" data-testid="website-title">
            {site?.name || "…"}
          </h1>
          <a
            href={site?.domain ? `https://${site.domain}` : "#"}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-[var(--rt-text-muted)] mt-2 inline-block font-mono-data hover:text-[var(--rt-primary)]"
          >
            {site?.domain}
          </a>
        </div>
        <div className="flex items-center gap-2">
          {site && <ShareButton website={site} />}
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                data-testid="delete-website-btn"
                variant="ghost"
                className="rounded-none text-[var(--rt-text-muted)] hover:text-[var(--rt-danger)] hover:bg-transparent h-10 px-3"
              >
                <Trash size={16} />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="rounded-none border border-[var(--rt-border)] bg-white">
              <AlertDialogHeader>
                <AlertDialogTitle className="font-display">Delete website?</AlertDialogTitle>
                <AlertDialogDescription>
                  This removes all keywords and rank history for {site?.domain}. This action is irreversible.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="rounded-none">Cancel</AlertDialogCancel>
                <AlertDialogAction
                  data-testid="confirm-delete-website"
                  onClick={() => delSiteMut.mutate()}
                  className="rounded-none bg-[var(--rt-danger)] hover:bg-red-700 text-white"
                >
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      <Tabs defaultValue="google" className="mt-2">
        <TabsList className="rounded-none bg-[var(--rt-surface-hover)] p-0 h-11 grid grid-cols-2 max-w-md mb-6">
          <TabsTrigger
            value="google"
            data-testid="tab-google"
            className="rounded-none data-[state=active]:bg-white data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-[var(--rt-primary)] h-full"
          >
            Google rankings
          </TabsTrigger>
          <TabsTrigger
            value="ai"
            data-testid="tab-ai"
            className="rounded-none data-[state=active]:bg-white data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-[var(--rt-primary)] h-full"
          >
            <span className="inline-flex items-center gap-2">
              AI visibility <span className="text-[9px] font-mono-data bg-[#F4B400] text-black px-1.5 py-0.5">NEW</span>
            </span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="google" className="mt-0">
          <div className="flex items-center justify-end gap-2 mb-6">
            <button
              data-testid="copy-keywords-btn"
              onClick={async () => {
                if (keywords.length === 0) return;
                try {
                  await navigator.clipboard.writeText(keywords.map((k) => k.keyword).join("\n"));
                  toast.success(`Copied ${keywords.length} keyword(s) to clipboard`);
                } catch {
                  toast.error("Could not copy");
                }
              }}
              disabled={keywords.length === 0}
              className={`rounded-none bg-white border border-[var(--rt-border)] text-[var(--rt-text)] hover:bg-[var(--rt-surface-hover)] h-10 px-4 inline-flex items-center gap-2 text-sm font-medium ${keywords.length === 0 ? "opacity-50 pointer-events-none" : ""}`}
              title="Copy all keywords to clipboard"
            >
              <Copy size={14} weight="bold" />
              Copy list
            </button>
            <Button
              data-testid="check-all-btn"
              onClick={() => checkAllMut.mutate()}
              disabled={isJobRunning || checkAllMut.isPending || keywords.length === 0}
              className="rounded-none bg-white border border-[var(--rt-border)] text-[var(--rt-text)] hover:bg-[var(--rt-surface-hover)] h-10 px-4 flex items-center gap-2"
            >
              <ArrowsClockwise size={14} weight={isJobRunning ? "regular" : "bold"} className={isJobRunning ? "animate-spin" : ""} />
              {isJobRunning
                ? `Checking ${jobStatus?.completed || 0}/${jobStatus?.total || "…"}`
                : "Check all now"}
            </Button>
            <a
              data-testid="csv-export-btn"
              href={exportCsvUrl(id)}
              download
              className={`rounded-none bg-white border border-[var(--rt-border)] text-[var(--rt-text)] hover:bg-[var(--rt-surface-hover)] h-10 px-4 inline-flex items-center gap-2 text-sm font-medium ${keywords.length === 0 ? "opacity-50 pointer-events-none" : ""}`}
            >
              <DownloadSimple size={14} weight="bold" />
              CSV
            </a>
            <a
              data-testid="pdf-export-btn"
              href={reportPdfUrl(id)}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-none bg-[var(--rt-text)] hover:bg-zinc-800 text-white h-10 px-4 inline-flex items-center gap-2 text-sm font-medium"
            >
              <FilePdf size={14} weight="bold" />
              PDF report
            </a>
            <AddKeywordsDialog websiteId={id} remaining={remaining} />
          </div>

      {/* Progress banner while job is running */}
      {isJobRunning && (
        <div
          data-testid="check-job-banner"
          className="border border-[var(--rt-primary)] bg-white p-5 mb-8 flex items-center gap-5"
        >
          <ArrowsClockwise size={22} className="animate-spin text-[var(--rt-primary)] shrink-0" />
          <div className="flex-1">
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="label-eyebrow text-[var(--rt-primary)]">/ background job</div>
                <div className="font-display text-xl mt-1 leading-none">
                  Fetching live Google ranks…
                </div>
              </div>
              <div className="font-mono-data text-sm" data-testid="job-progress-text">
                {jobStatus?.completed || 0} / {jobStatus?.total || 0}
                {jobStatus?.failed > 0 && (
                  <span className="text-[var(--rt-danger)] ml-2">· {jobStatus.failed} failed</span>
                )}
              </div>
            </div>
            <div className="h-1.5 bg-[var(--rt-border)] relative overflow-hidden">
              <div
                className="h-full bg-[var(--rt-primary)] transition-all duration-500"
                style={{ width: `${jobPct}%` }}
                data-testid="job-progress-bar"
              />
            </div>
            <div className="text-xs text-[var(--rt-text-muted)] mt-2">
              Running in the background. You can close this tab or navigate away — progress is saved.
            </div>
          </div>
        </div>
      )}

      {/* KPI strip */}
      <div className="border border-[var(--rt-border)] bg-white p-6 grid grid-cols-2 md:grid-cols-5 gap-6 mb-8">
        <div>
          <div className="label-eyebrow">KEYWORDS</div>
          <div className="font-display text-3xl mt-1 leading-none">{site?.keyword_count ?? 0}/200</div>
        </div>
        <div>
          <div className="label-eyebrow">AVG POSITION</div>
          <div className="font-display text-3xl mt-1 leading-none">{site?.avg_position ?? "—"}</div>
        </div>
        <div>
          <div className="label-eyebrow">IN TOP 3</div>
          <div className="font-display text-3xl mt-1 leading-none text-[var(--rt-success)]">{site?.in_top_3 ?? 0}</div>
        </div>
        <div>
          <div className="label-eyebrow">IN TOP 10</div>
          <div className="font-display text-3xl mt-1 leading-none text-[var(--rt-primary)]">{site?.in_top_10 ?? 0}</div>
        </div>
        <div>
          <div className="label-eyebrow">NOT RANKING</div>
          <div className="font-display text-3xl mt-1 leading-none text-[var(--rt-text-muted)]">
            {keywords.filter((k) => k.last_position == null).length}
          </div>
        </div>
      </div>

      {/* Filter + Table */}
      <div className="flex items-center justify-between mb-3">
        <div className="label-eyebrow">/ keywords</div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger
            data-testid="filter-select"
            className="w-44 rounded-none border-[var(--rt-border)] bg-white h-9"
          >
            <SelectValue placeholder="Filter" />
          </SelectTrigger>
          <SelectContent className="rounded-none border-[var(--rt-border)]">
            <SelectItem value="all">All keywords</SelectItem>
            <SelectItem value="top3">Top 3</SelectItem>
            <SelectItem value="top10">Top 10</SelectItem>
            <SelectItem value="top50">Top 50</SelectItem>
            <SelectItem value="missing">Not ranking</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="border border-[var(--rt-border)] bg-white">
        <div className="grid grid-cols-12 gap-4 px-4 py-3 label-eyebrow border-b border-[var(--rt-border)] bg-[#FAFAFA]">
          <div className="col-span-5">Keyword</div>
          <div className="col-span-1 text-right">Pos.</div>
          <div className="col-span-1 text-right">Delta</div>
          <div className="col-span-3 truncate">Matched URL</div>
          <div className="col-span-1 text-right">Last check</div>
          <div className="col-span-1 text-right">Actions</div>
        </div>

        {isLoading ? (
          <div className="px-4 py-10 text-center text-sm text-[var(--rt-text-muted)]">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="px-4 py-12 text-center" data-testid="no-keywords">
            <div className="label-eyebrow">/ no keywords</div>
            <p className="text-sm text-[var(--rt-text-muted)] mt-2">
              {keywords.length === 0 ? "Add keywords to start tracking." : "No keywords match this filter."}
            </p>
          </div>
        ) : (
          <div>
            {filtered.map((k) => (
              <div
                key={k.id}
                data-testid={`kw-row-${k.id}`}
                className="grid grid-cols-12 gap-4 px-4 py-3 border-b border-[var(--rt-border)] last:border-b-0 rt-row-zebra hover:bg-[var(--rt-surface-hover)] items-center"
              >
                <div className="col-span-5">
                  <Link
                    to={`/keywords/${k.id}`}
                    className="text-sm hover:text-[var(--rt-primary)] block truncate select-text"
                    data-testid={`kw-link-${k.id}`}
                    onClick={(e) => {
                      // Let the user select text without navigating
                      if (window.getSelection()?.toString()) e.preventDefault();
                    }}
                  >
                    {k.keyword}
                  </Link>
                </div>
                <div className="col-span-1 text-right">
                  <PositionBadge position={k.last_position} />
                </div>
                <div className="col-span-1 text-right">
                  <RankDelta current={k.last_position} previous={k.prev_position} />
                </div>
                <div className="col-span-3 truncate">
                  {k.last_matched_url ? (
                    <a
                      href={k.last_matched_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-[var(--rt-text-muted)] hover:text-[var(--rt-primary)] font-mono-data truncate inline-block max-w-full"
                    >
                      {k.last_matched_url.replace(/^https?:\/\//, "")}
                    </a>
                  ) : (
                    <span className="text-xs text-[var(--rt-text-muted)]">—</span>
                  )}
                </div>
                <div className="col-span-1 text-right text-xs font-mono-data text-[var(--rt-text-muted)]">
                  {k.last_checked_at ? new Date(k.last_checked_at).toLocaleDateString() : "—"}
                </div>
                <div className="col-span-1 flex items-center justify-end gap-1">
                  <button
                    data-testid={`check-kw-${k.id}`}
                    onClick={() => checkOneMut.mutate(k.id)}
                    disabled={checkOneMut.isPending}
                    className="p-1.5 hover:bg-[var(--rt-surface-hover)] text-[var(--rt-text-muted)] hover:text-[var(--rt-primary)] transition-colors"
                    title="Check now"
                  >
                    <ArrowsClockwise size={14} />
                  </button>
                  <button
                    data-testid={`del-kw-${k.id}`}
                    onClick={() => delKwMut.mutate(k.id)}
                    className="p-1.5 hover:bg-[var(--rt-surface-hover)] text-[var(--rt-text-muted)] hover:text-[var(--rt-danger)] transition-colors"
                    title="Delete"
                  >
                    <Trash size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
        </TabsContent>

        <TabsContent value="ai" className="mt-0">
          <AiVisibilityTab websiteId={id} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
